import { makeAutoObservable } from 'mobx'
import { NavigateFunction } from 'react-router-dom'
import { isSameYear, isToday, isYesterday, parseISO } from 'date-fns'

import { EventApi } from 'api'
import { EventType, IBaby, IEvent, Nullable } from 'types'
import { pageSize } from 'consts'
import { PaginationStore } from 'stores'
import { notification } from 'services'

import { serializeEvent } from './EventStore/utils'

import { formatDate } from 'utils'

const boundEventTypes = [EventType.BOTTLE, EventType.BREAST_LEFT, EventType.BREAST_RIGHT]

const calcTotalPages = (currTotalPages: number, newItemsCount: number) =>
  newItemsCount < pageSize ? currTotalPages : currTotalPages + 1

const initTotalPages = (itemsCount: number) => (itemsCount < pageSize ? 1 : 2)

export default class HomeStore {
  loading = false
  currentLoading = false
  loaded = false
  events: IEvent[] = []
  currentEvents: IEvent[] = []
  baby: Nullable<IBaby> = null
  loadingMore = false
  pagination: PaginationStore
  navigate: Nullable<NavigateFunction>
  getLastEvents: Nullable<() => void>

  constructor(getLastEvents: () => void, navigate?: NavigateFunction) {
    this.pagination = new PaginationStore()
    this.navigate = navigate
    this.getLastEvents = getLastEvents
    makeAutoObservable(this, undefined, { autoBind: true })
  }

  setBaby = (baby: IBaby) => {
    this.baby = baby
  }

  addEventOnFrontend = (event: IEvent) => {
    this.events = [event, ...this.events]
  }

  getLastStartTime = () => {
    if (this.events.length) {
      return this.events[this.events.length - 1].startTime || null
    }
    return null
  };

  *loadCurrentEvents() {
    if (this.baby?.id) {
      this.currentLoading = true

      try {
        const { data } = yield EventApi.getEvents(this.baby.id, false)
        this.currentEvents = data.data
        this.refreshDuration()
      } catch {
        notification.error('Ошибка загрузки текущих событий')
      } finally {
        this.currentLoading = false
      }
    }
  }

  refreshDuration = () => {
    this.currentEvents = this.currentEvents.map((v) => ({
      ...v,
      endTime: new Date().toISOString(),
    }))
  }

  get groupedEvents() {
    if (!this.events.length) return []

    const today = new Date()

    const groups: Record<string, IEvent[]> = this.events.reduce(
      (acc, event) => {
        const isoDate = event.startTime
        const date = parseISO(isoDate)
        let label = ''
        const dayAndMonth = formatDate(isoDate, 'd MMMM')

        if (isToday(date)) {
          label = `Сегодня: ${dayAndMonth}`
        } else if (isYesterday(date)) {
          label = dayAndMonth
        } else {
          if (isSameYear(date, today)) {
            label = dayAndMonth
          } else {
            label = formatDate(isoDate, 'd MMMM yyyy')
          }
        }

        if (!acc[label]) {
          acc[label] = []
        }

        acc[label].push(event)

        return acc
      },
      {} as Record<string, IEvent[]>,
    )

    return groups
  }

  *loadEvents() {
    try {
      if (this.baby?.id) {
        this.loading = true

        const { data } = yield EventApi.getEvents(this.baby.id, true)
        this.pagination.totalPages = initTotalPages(data.data.length)
        this.pagination.currentPage = 1

        this.events = data.data
      }
    } catch {
      notification.error('Ошибка загрузки событий')
    } finally {
      this.loading = false
      this.loaded = true
    }
  }

  *loadMoreEvents() {
    if (this.loadingMore) return
    const minStartTime = this.getLastStartTime()

    if (this.baby?.id && minStartTime) {
      this.loadingMore = true

      try {
        const { data } = yield EventApi.getMoreEvents(this.baby.id, minStartTime)
        this.pagination.totalPages = calcTotalPages(this.pagination.totalPages, data.data.length)
        this.events = [...this.events, ...data.data]
      } catch {
        notification.error('Ошибка загрузки событий')
      } finally {
        this.loadingMore = false
      }
    }
  }

  *toggleEvent(event: IEvent, type: EventType) {
    const existingEvent = this.currentEvents.find((v) => v.eventTypeCode === type)

    // Если добавляем событие кормления,
    // останавливаем при этом все текущие события кормления
    if (!existingEvent) {
      if (boundEventTypes.includes(type)) {
        const boundEvents = this.currentEvents.filter(
          (v) => v.eventTypeCode && boundEventTypes.includes(v.eventTypeCode),
        )

        yield Promise.allSettled(
          boundEvents.map(({ id: eventId }) =>
            eventId ? this.stopEvent(eventId) : Promise.resolve(),
          ),
        )
      }

      const data: IEvent = yield this.createEvent(event, type)
      this.currentEvents = [data, ...this.currentEvents]
    } else if (existingEvent.id) {
      yield this.stopEvent(existingEvent.id)
    }
  }

  *stopEvent(currentEventId: number) {
    const foundEvent = this.currentEvents.find((v) => v.id === currentEventId)

    if (this.baby?.id && foundEvent) {
      this.loading = true

      if (!foundEvent.endTime) {
        foundEvent.endTime = new Date().toISOString()
      }

      try {
        yield EventApi.editEvent(this.baby.id, foundEvent.id!.toString(), foundEvent)

        this.currentEvents = this.currentEvents.filter((v) => v.id !== currentEventId)
        this.addEventOnFrontend(foundEvent)
        this.getLastEvents?.()

        if (foundEvent.eventTypeCode === EventType.BOTTLE) {
          this.navigate?.(`/event/${foundEvent.id}`, { state: { event: { ...foundEvent } } })
        }
      } catch {
        notification.error('Ошибка остановки события')
      } finally {
        this.loading = false
      }
    }
  }

  *createEvent(event: IEvent, type: EventType) {
    if (this.baby?.id) {
      this.loading = true
      const serialized = serializeEvent(event as IEvent, type as EventType)
      try {
        const { data } = yield EventApi.createEvent(this.baby.id, serialized)

        return data.data
      } catch {
        notification.error('Ошибка добавления события')
        return null
      } finally {
        this.loading = false
      }
    }
  }
}
